import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import ManualTimeOffBanner from '../../../components/common/ManualTimeOffBanner'
import { useDispatch, useSelector } from 'react-redux'
import LabeledValue from '../../../components/common/LabeledValue'
import { TimeOffRequestDetails ,isVacationBid} from '../../../store/slices/Requestor/timeOffDetailsSlice'
import { normalizeStatus, buildTableRows } from './detail/constants'
import { formatDateToMMDDYYYY } from '../../../utils/date'
import RightPanel from './detail/RightPanel'
import CurrentRequestSection from './detail/CurrentRequestSection'
import ApprovedView from './detail/ApprovedView'
import SubmittedOrOnHoldView from './detail/SubmittedOrOnHoldView'
import ReturnedView from './detail/ReturnedView'
import DeniedView from './detail/DeniedView'
import WithdrawnView from './detail/WithdrawnView'
import RetractedView from './detail/RetractedView'
import PartiallyApprovedView from './detail/PartiallyApprovedView'
import DraftView from './detail/DraftView'
import OverriddenView from './detail/OverriddenView'
import PartiallyApprovedTimeOffView from './detail/PartiallyApprovedTimeOffView'
import PartiallyDeniedTimeOffView from './detail/PartiallyDeniedTimeOffView'
import ResubmittedTimeOffView from './detail/ResubmittedTimeOffView'
import ResubmittedVacationBidView from './detail/ResubmittedVacationBidView'
import PartiallyApprovedRetractionDeniedView from './detail/PartialApprovedRetractionDeniedView'
import PartialRetractedView from './detail/PartialRetractedView'
import { usePrint } from '../../../hooks/usePrint'
import { getOtherReqDetails } from '../../../store/slices/Requestor/createNewRequestSlice'
import { RootState, AppDispatch } from '../../../store/store'
import PartialRetractionDeniedOverriden from './detail/PartialRetractionDeniedOverriden'
import { isRetractionDeniedStatus } from '../../../utils/statusFieldsUtils'

interface Props {
  data: TimeOffRequestDetails
}

interface StatusViewArgs {
  ns: string
  status: string
  vacationBid: boolean
  sharedProps: {
    data: TimeOffRequestDetails
    tableRows: ReturnType<typeof buildTableRows>
    highlightedDates: string[]
    handlePrint: () => void
  }
}

type SharedProps = StatusViewArgs['sharedProps']

const RESUBMITTED_VACATION_BID_STATUSES = new Set([
  'resubmitted_onhold',
  'resubmitted_released',
])

const STATUS_VIEW_MAP: Record<string, (props: SharedProps) => React.ReactNode> = {
  approved: (props) => <ApprovedView {...props} />,
  rtsubmitted: (props) => <ApprovedView {...props} />,
  prtsubmitted: (props) => <ApprovedView {...props} />,
  approved_prtsubmitted: (props) => <ApprovedView {...props} />,
  rtsubmitted_released: (props) => <ApprovedView {...props} />,
  prtsubmitted_released: (props) => <ApprovedView {...props} />,
  submitted: (props) => <SubmittedOrOnHoldView {...props} />,
  'on hold': (props) => <SubmittedOrOnHoldView {...props} />,
  submitted_onhold: (props) => <SubmittedOrOnHoldView {...props} />,
  rtsubmitted_onhold: (props) => <SubmittedOrOnHoldView {...props} />,
  prtsubmitted_onhold: (props) => <SubmittedOrOnHoldView {...props} />,
  submitted_released: (props) => <SubmittedOrOnHoldView {...props} />,
  returned: (props) => <ReturnedView {...props} />,
  withdrawn: (props) => <WithdrawnView {...props} />,
  retracted: (props) => <RetractedView {...props} />,
  rtdenied_overridden: (props) => <RetractedView {...props} />,
  'partially approved': (props) => <PartiallyApprovedView {...props} />,
  papproved: (props) => <PartiallyApprovedTimeOffView {...props} />,
  papproved_rtdenied: (props) => <PartiallyApprovedRetractionDeniedView {...props} />,
  pdenied: (props) => <PartiallyDeniedTimeOffView {...props} />,
  draft: (props) => <DraftView {...props} />,
  'draft request': (props) => <DraftView {...props} />,
  overridden: (props) => <OverriddenView {...props} />,
  pretracted: (props) => <PartialRetractedView {...props} />,
  prtdenied: (props) => <PartialRetractedView {...props} />,
  prtdenied_overridden: (props) => <PartialRetractionDeniedOverriden {...props} />,
  resubmitted_onhold: (props) => <ResubmittedTimeOffView {...props} />,
  resubmitted: (props) => <ResubmittedTimeOffView {...props} />,
  resubmitted_released: (props) => <ResubmittedTimeOffView {...props} />,
  resubmitted_release: (props) => <ResubmittedTimeOffView {...props} />,
}

const resolveStatusView = ({
  ns,
  status,
  vacationBid,
  sharedProps,
}: StatusViewArgs): React.ReactNode | null => {
  if (ns === 'denied' || isRetractionDeniedStatus(status)) {
    return <DeniedView {...sharedProps} />
  }

  if (vacationBid && RESUBMITTED_VACATION_BID_STATUSES.has(ns)) {
    return <ResubmittedVacationBidView {...sharedProps} />
  }

  const renderStatusView = STATUS_VIEW_MAP[ns]
  if (renderStatusView) {
    return renderStatusView(sharedProps)
  }

  return null
}

const TimeOffRequestDetail: React.FC<Props> = ({ data }) => {



  const dispatch = useDispatch<AppDispatch>()

  const otherReqDetails = useSelector(
    (state: RootState) => state.otherReqDetails?.data || {}
  )

  useEffect(() => {
    const firstDate = data.requestedDates?.[0]?.date

    if (!firstDate) return

    const [year, month] = firstDate.split('-')

    dispatch(
      getOtherReqDetails({
        month: Number(month) - 1,
        year: Number(year),
      })
    )
  }, [data.requestedDates, dispatch])
  const { printRef, handlePrint } = usePrint()
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const tableRows = buildTableRows(data.requestedDates, otherReqDetails, data.status)
  // Convert dates to MM/DD/YYYY format for Datepicker
  const highlightedDates = (data.requestedDates ?? [])
    .map((d) => formatDateToMMDDYYYY(d.date))
    .filter(Boolean)
  const ns = normalizeStatus(data.status)

  const location = useLocation()
  const isAdminMode = location?.pathname?.startsWith?.('/admin') || false

  const sharedProps = { data, tableRows, highlightedDates, handlePrint }
  const vacationBid = isVacationBid(data)
  const resolvedStatusView = resolveStatusView({
    ns,
    status: data.status,
    vacationBid,
    sharedProps,
  })

  const statusView = resolvedStatusView ?? (
    <div>
      <h1 className="h1">Time Off Request</h1>
      <div className="flex flex-col mobile:gap-4 tablet:gap-6 desktop:gap-8 desktop:flex-row items-start">
        <div className="flex-1">
          {data.submittedOn && <div className="mb-4"><LabeledValue label="Submitted On" value={data.submittedOn} /></div>}
          {data.submittedBy && <div className="mb-4"><LabeledValue label="Submitted By" value={data.submittedBy} /></div>}
          {data.employeeComments && <div className="mb-4"><LabeledValue label="Employee Comments" value={data.employeeComments} /></div>}
        </div>
        <RightPanel data={data} handlePrint={handlePrint} />
      </div>
      <div className="mt-6!"><hr className="-variation-1" /></div>
      <CurrentRequestSection tableRows={tableRows} highlightedDates={highlightedDates} />
    </div>
  )

  return (
    <div ref={printRef}>
      {isAdminMode && <div className='mt-12!'><ManualTimeOffBanner /></div>}
      {statusView}
      {ns !== 'overridden' && data.partiallyDeniedOn && (
        <div className="mt-6 flex flex-col mobile:gap-4 tablet:gap-6 desktop:gap-8 desktop:flex-row">
          <div className="flex-1">
            <div className="mb-4"><LabeledValue label="Partially Denied On" value={data.partiallyDeniedOn} /></div>
            {data.partialRefId && (
              <div className="mb-4"><LabeledValue label="Approved Time Off Request ID" value={data.partialRefId} /></div>
            )}
            {data.partiallyDeniedApprover && (
              <div className="mb-4"><LabeledValue label="Authorized Approver" value={data.partiallyDeniedApprover} /></div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default TimeOffRequestDetail
